import { useState, useEffect } from "react";
import tw from "twin.macro";

// Posts to the EthosHub app's /api/contact route, which stores the lead and
// emails it. Replaces the old Zoho Web-to-Contact iframe, which shipped
// placeholder form IDs ("YOUR_xnQsjsdp") so every submission was rejected,
// while the UI still showed a success toast on iframe load.
const API_URL = process.env.REACT_APP_CONTACT_API_URL
  ? `${process.env.REACT_APP_CONTACT_API_URL}/api/contact`
  : "/api/contact";

function Toast({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(onClose, 3500);
    return () => clearTimeout(id);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-green-600 text-white px-4 py-2 shadow"
    >
      {children}
    </div>
  );
}

const Input = tw.input`
  w-full px-4 py-3 rounded-3xl bg-white text-gray-900 border border-gray-300
  focus:outline-none focus:ring-2 focus:ring-primary-500
`;
const Button = tw.button`
  w-full px-5 py-3 rounded-3xl bg-primary-500 text-white font-semibold
  hover:bg-primary-700 transition disabled:opacity-60 disabled:cursor-not-allowed
`;
const Label = tw.label`text-xs text-gray-800 flex items-start gap-2`;
const ErrorText = tw.div`text-red-600 text-xs mt-1`;

export default function NewsletterForm() {
  const [fields, setFields] = useState({ firstName: "", lastName: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setErrorMsg("Please accept the Privacy Policy and Terms.");
      return;
    }
    setErrorMsg("");
    setStatus("sending");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...fields, source: "newsletter", website_url: honeypot }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Subscription failed");
      setStatus("sent");
      setFields({ firstName: "", lastName: "", email: "" });
      setConsent(false);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="relative w-full max-w-sm mx-auto lg:mx-0 mt-6">
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <Input
          placeholder="First name"
          aria-label="First name"
          value={fields.firstName}
          onChange={e => set("firstName", e.target.value)}
        />
        <Input
          placeholder="Last name"
          aria-label="Last name"
          value={fields.lastName}
          onChange={e => set("lastName", e.target.value)}
        />
        <Input
          type="email"
          required
          placeholder="Email *"
          aria-label="Email"
          value={fields.email}
          onChange={e => set("email", e.target.value)}
        />

        {/* Honeypot: hidden from humans, bots fill it and the server drops the submission */}
        <input
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
          value={honeypot}
          onChange={e => setHoneypot(e.target.value)}
        />

        <div className="mt-2">
          <Label>
            <input
              type="checkbox"
              className="mt-0.5"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline text-primary-300">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="/terms-of-service" target="_blank" rel="noreferrer" className="underline text-primary-300">
                Terms of Service
              </a>.
            </span>
          </Label>
          {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
        </div>

        <Button type="submit" disabled={status === "sending"} aria-label="Subscribe">
          {status === "sending" ? "Sending…" : "Subscribe"}
        </Button>
      </form>

      <Toast open={status === "sent"} onClose={() => setStatus("idle")}>
        Thanks! You&rsquo;re on the list.
      </Toast>
    </div>
  );
}
